import React, { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence, useAnimationFrame } from "framer-motion";
import { X } from "lucide-react";
import { stories } from "../../data/reportData";
import { CircledWord } from "../CircledWord";

const fadeUp = {
  initial: { opacity: 0, y: 36 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
};

const GREEN = "#53B851";

const FIELDS = [
  { key: "who", label: "WHO" },
  { key: "problem", label: "THE PROBLEM" },
  { key: "built", label: "WHAT THEY BUILT" },
  { key: "impact", label: "THE IMPACT" },
];

// Operator photos supplied by the user — each tile maps a photo to its story
const TILE_MEDIA = [
  { img: "/assets/stories/story-1.webp", story: 0 },
  { img: "/assets/stories/story-2.webp", story: 1 },
  { img: "/assets/stories/story-3.webp", story: 2 },
  { img: "/assets/stories/story-4.webp", story: 3 },
  { img: "/assets/stories/story-5.webp", story: 4 },
  { img: "/assets/stories/story-6.webp", story: 5 },
  { img: "/assets/stories/story-7.webp", story: 6 },
  { img: "/assets/stories/story-8.webp", story: 7 },
  { img: "/assets/stories/story-9.webp", story: 7 },
  { img: "/assets/stories/story-10.webp", story: 8 },
];

const ROW_ANGLES = [-20, 0, 20];
const COLS = 16;
const COL_STEP = 360 / COLS;

// deterministic "random" spread: photos repeating without adjacent repeats
const tileMedia = (row, col) => (col * 3 + row * 2) % TILE_MEDIA.length;

function StoryPanel({ tile, onClose }) {
  const story = stories[tile.story];
  return (
    <motion.div
      className="absolute inset-0 z-30 flex items-center justify-center px-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="story-panel"
    >
      <div className="absolute inset-0 bg-black/75" onClick={onClose} data-testid="story-panel-backdrop" />
      <motion.div
        initial={{ opacity: 0, scale: 0.72, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.82, y: 12 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-[760px] max-w-[94vw] max-h-[86%] overflow-y-auto bg-[#101211] border border-white/12 rounded-2xl grid md:grid-cols-[280px_1fr]"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center border border-white/25 text-white/80 hover:text-white hover:border-white/60 transition-colors rounded-full"
          data-testid="story-panel-close"
          aria-label="Close story"
        >
          <X size={16} />
        </button>
        <div className="p-7 md:p-8 flex flex-col justify-between border-b md:border-b-0 md:border-r border-white/10">
          <div>
            <p className="text-[11px] tracking-[0.18em] text-white/35">
              {String(tile.story + 1).padStart(2, "0")}
            </p>
            <p className="font-display font-semibold text-[44px] leading-none tracking-[-0.03em] mt-6" style={{ color: GREEN }}>
              {story.metric}
            </p>
            <p className="text-[13.5px] text-white/55 mt-3 leading-snug">{story.metricLabel}</p>
          </div>
          <div className="mt-8">
            <p className="font-display font-medium text-[20px] text-white leading-tight">{story.business}</p>
            <p className="text-[12px] text-white/45 mt-2">{story.name}{story.location ? ` · ${story.location}` : ""}</p>
          </div>
        </div>
        <div className="p-7 md:p-8 flex flex-col justify-center gap-5">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <p className="text-[10px] tracking-[0.18em] mb-1.5" style={{ color: f.key === "impact" ? GREEN : "rgba(255,255,255,0.4)" }}>
                {f.label}
              </p>
              <p className={`text-[13px] leading-[1.6] ${f.key === "impact" ? "text-white" : "text-white/70"}`}>
                {story[f.key]}
              </p>
            </div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

function StoryDome() {
  const viewportRef = useRef(null);
  const worldRef = useRef(null);
  const rot = useRef({ x: -2, y: 0 });
  const drag = useRef(null);
  const movedRef = useRef(false);
  const hoverRef = useRef(false);
  const velY = useRef(0);
  const [radius, setRadius] = useState(760);
  const radiusRef = useRef(760);
  radiusRef.current = radius;
  const [open, setOpen] = useState(null);
  const openRef = useRef(null);
  openRef.current = open;

  useEffect(() => {
    const upd = () => {
      const w = viewportRef.current ? viewportRef.current.offsetWidth : 1200;
      setRadius(Math.min(950, Math.max(420, w * 0.5)));
    };
    upd();
    window.addEventListener("resize", upd);
    return () => window.removeEventListener("resize", upd);
  }, []);

  // tile size derived from sphere geometry so tiles never touch, at any radius
  const tileW = Math.round(2 * radius * Math.sin(((COL_STEP / 2) * Math.PI) / 180) * 0.8);
  const tileH = Math.round(2 * radius * Math.sin((10 * Math.PI) / 180) * 0.76);

  useAnimationFrame((t, delta) => {
    const dt = Math.min(delta, 50) / 1000;
    if (!drag.current) {
      if (Math.abs(velY.current) > 0.05) {
        rot.current.y += velY.current * dt;
        velY.current *= Math.pow(0.04, dt);
      } else if (openRef.current === null && !hoverRef.current) {
        rot.current.y -= 3.0 * dt;
      }
      rot.current.x += (-2 - rot.current.x) * Math.min(1, dt * 1.5);
    }
    if (worldRef.current) {
      worldRef.current.style.transform = `translateZ(${-radiusRef.current}px) rotateX(${rot.current.x}deg) rotateY(${rot.current.y}deg)`;
    }
  });

  const onPointerDown = (e) => {
    if (openRef.current !== null) return;
    drag.current = { x: e.clientX, y: e.clientY, lastX: e.clientX, lastT: performance.now() };
    movedRef.current = false;
    velY.current = 0;
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.lastX;
    if (Math.abs(e.clientX - d.x) + Math.abs(e.clientY - d.y) > 7) movedRef.current = true;
    rot.current.y += dx * 0.12;
    rot.current.x = Math.max(-9, Math.min(9, rot.current.x - (e.clientY - d.y) * 0.002));
    const now = performance.now();
    const dtms = Math.max(1, now - d.lastT);
    velY.current = (dx * 0.12) / (dtms / 1000);
    velY.current = Math.max(-140, Math.min(140, velY.current));
    d.lastX = e.clientX;
    d.lastT = now;
  };
  const endDrag = () => { drag.current = null; };

  const tiles = [];
  ROW_ANGLES.forEach((rowAng, r) => {
    for (let c = 0; c < COLS; c++) {
      tiles.push({ row: r, rowAng, colAng: c * COL_STEP, media: tileMedia(r, c), story: TILE_MEDIA[tileMedia(r, c)].story, key: `${r}-${c}` });
    }
  });

  return (
    <div
      ref={viewportRef}
      className="relative mt-10 md:mt-14 mx-[2vw] md:mx-[3vw] h-[500px] md:h-[640px] overflow-hidden select-none cursor-grab active:cursor-grabbing"
      style={{ perspective: "1200px", touchAction: "pan-y" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerLeave={() => { endDrag(); hoverRef.current = false; }}
      onPointerCancel={endDrag}
      onMouseEnter={() => { hoverRef.current = true; }}
      data-testid="stories-dome"
    >
      <div
        ref={worldRef}
        className="absolute left-1/2 top-1/2 will-change-transform"
        style={{ transformStyle: "preserve-3d" }}
      >
        {tiles.map((tile) => (
          <div
            key={tile.key}
            className="absolute"
            style={{
              transform: `rotateY(${tile.colAng}deg) rotateX(${tile.rowAng}deg) translateZ(${radius}px)`,
              transformStyle: "preserve-3d",
              backfaceVisibility: "hidden",
              width: tileW,
              height: tileH,
              left: -tileW / 2,
              top: -tileH / 2,
            }}
          >
            <button
              onClick={() => { if (!movedRef.current) setOpen(tile); }}
              className="group relative block w-full h-full overflow-hidden focus:outline-none"
              style={{
                background: "#161616",
                borderRadius: 10,
                ...(tile.rowAng > 0
                  ? { WebkitMaskImage: "linear-gradient(to top, black 0%, rgba(0,0,0,0.3) 100%)", maskImage: "linear-gradient(to top, black 0%, rgba(0,0,0,0.3) 100%)" }
                  : tile.rowAng < 0
                    ? { WebkitMaskImage: "linear-gradient(to bottom, black 0%, rgba(0,0,0,0.3) 100%)", maskImage: "linear-gradient(to bottom, black 0%, rgba(0,0,0,0.3) 100%)" }
                    : {}),
              }}
              data-testid={`story-tile-${tile.key}`}
              tabIndex={-1}
              aria-label={`Read the story of ${stories[tile.story].business}`}
            >
              <img
                src={TILE_MEDIA[tile.media].img}
                alt=""
                draggable={false}
                className="w-full h-full object-cover pointer-events-none"
                style={{ filter: "grayscale(1) brightness(0.72) contrast(1.05)" }}
                loading="lazy"
              />
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                style={{ background: "rgba(83,184,81,0.55)", mixBlendMode: "multiply" }}
              />
            </button>
          </div>
        ))}
      </div>

      {/* vignette */}
      <div
        className="pointer-events-none absolute inset-0 z-10"
        style={{
          background:
            "radial-gradient(ellipse 74% 60% at 50% 50%, rgba(0,0,0,0) 52%, rgba(0,0,0,0.6) 76%, rgba(0,0,0,0.92) 91%, #000 99%), linear-gradient(to bottom, #000 0%, rgba(0,0,0,0) 14%, rgba(0,0,0,0) 84%, #000 100%)",
        }}
      />

      <AnimatePresence>
        {open !== null && <StoryPanel tile={open} onClose={() => setOpen(null)} />}
      </AnimatePresence>
    </div>
  );
}

export default function StoriesSection() {
  return (
    <section id="stories" className="bg-black text-white mt-24 md:mt-36" data-testid="section-stories">
      <div className="py-20 md:py-24">
        <div className="report-col">
          <motion.p
            {...fadeUp}
            className="uppercase text-[12.5px] tracking-[0.2em] font-medium mb-5"
            style={{ color: GREEN }}
          >
            Operator stories
          </motion.p>
          <motion.h2 {...fadeUp} className="section-h2" style={{ color: "#FFFFFF" }}>
            The businesses behind the <CircledWord color={GREEN}>numbers</CircledWord>
          </motion.h2>
          <motion.p {...fadeUp} className="text-[16px] md:text-[17px] leading-[1.75] text-white/65 mt-8 max-w-2xl">
            Nine operators, named with consent, on what broke, what they built, and what it changed. Figures in these stories are as reported by the operators themselves.
          </motion.p>
          <motion.p {...fadeUp} className="uppercase text-[12px] tracking-[0.16em] text-white/35 mt-6">
            Drag to explore. Click a card to <span className="font-semibold" style={{ color: GREEN }}>read the full story.</span>
          </motion.p>
        </div>
        <StoryDome />
      </div>
    </section>
  );
}
